// src/pages/Orders/OrderDetail.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot, collection, query, orderBy, writeBatch, serverTimestamp, getDoc, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { font } from './Roboto-Regular-font';
import {
    generateOrderPDF,
    generateShipmentPDF,
    generateCombinedShipmentsPDF,
    generateOrderFilename
} from '../../utils/pdfGenerator';
import ImageUpload from '../../components/ImageUpload';
import OrderActivity from '../../components/orders/OrderActivity';
import {
    logOrderUpdated,
    logOrderDeleted,
    logPaymentReceived,
    logOrderStatusChanged,
    logShipmentCreated,
    logItemsChanged
} from '../../utils/orderActivityLogger';
import { UsageLimitGuard } from '../../components/subscription/SubscriptionGuard';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#f8fafc',
        height: '100%',
        overflow: 'auto',
        padding: '32px'
    },
    button: {
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    exportButton: {
        padding: '12px 24px',
        border: '1px solid #e2e8f0',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(8px)',
        color: '#374151',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    input: { 
        padding: '12px 16px', 
        fontSize: '14px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '10px', 
        width: '100%', 
        background: '#fff', 
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    formGroup: { marginBottom: '20px' },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        marginTop: '16px'
    },
    th: {
        background: '#f1f5f9',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        fontWeight: '700',
        fontSize: '12px',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#334155',
        verticalAlign: 'middle'
    },
    statusBadge: (status) => {
        const colors = {
            'Beklemede': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
            'Onaylandı': { bg: '#dcfce7', text: '#166534', border: '#86efac' },
            'Kısmen Gönderildi': { bg: '#e0f2fe', text: '#075985', border: '#7dd3fc' },
            'Tamamlandı': { bg: '#dcfce7', text: '#06C167', border: '#4ade80' },
            'İptal Edildi': { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
            'Hazırlanıyor': { bg: '#fef9c3', text: '#854d0e', border: '#fde047' }
        };
        const color = colors[status] || { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
        return {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 14px',
            borderRadius: '10px',
            fontWeight: '700',
            fontSize: '11px',
            gap: '6px',
            background: color.bg,
            color: color.text,
            border: `1px solid ${color.border}`,
            textTransform: 'uppercase',
            letterSpacing: '0.03em'
        };
    },
    infoCard: {
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(12px)',
        padding: '28px',
        borderRadius: '20px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
        marginBottom: '24px',
        border: '1px solid rgba(255, 255, 255, 0.6)'
    },
    customerLink: {
        color: '#4f46e5',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: '600',
        transition: 'color 0.2s'
    },
    suggestions: { 
        position: 'absolute', 
        top: '100%', 
        left: 0, 
        right: 0, 
        background: 'rgba(255, 255, 255, 0.9)', 
        backdropFilter: 'blur(10px)',
        border: '1px solid #e2e8f0', 
        borderRadius: '12px', 
        maxHeight: '200px', 
        overflowY: 'auto', 
        zIndex: 1000, 
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' 
    },
    suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' },
    tabBar: {
        gap: '4px',
        marginBottom: '32px',
        background: '#f1f5f9',
        padding: '6px',
        borderRadius: '14px',
        width: 'fit-content',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        display: 'inline-flex'
    },
    tab: {
        padding: '10px 20px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        borderRadius: '10px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    activeTab: {
        background: 'white',
        color: '#4f46e5',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    gridTwo: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '32px'
    },
    secondaryButton: {
        padding: '10px 20px',
        background: 'white',
        border: '1px solid #e2e8f0',
        color: '#475569',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
};

const mobileStyles = `
    @media (max-width: 768px) {
        .responsive-table-wrapper {
            overflow-x: auto;
            margin: 0 -16px;
            padding: 0 16px;
        }
        .order-detail-grid {
            grid-template-columns: 1fr !important;
        }
        .header-actions {
            flex-wrap: wrap;
            gap: 8px;
        }
        .tab-bar {
            width: 100% !important;
            display: flex !important;
        }
    }
`;

const OrderDetail = () => {
    const { orderId } = useParams();
    const { userData, currentUser } = useAuth();
    const { hasRestriction } = useSubscription();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [shipments, setShipments] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');

    const [isEditing, setIsEditing] = useState(false);
    const [editedItems, setEditedItems] = useState([]);
    const [newItem, setNewItem] = useState({ productId: '', productName: '', quantity: 1, price: 0, vatRate: 0.20, imageUrl: '', imageData: null });
    const [productSuggestions, setProductSuggestions] = useState([]);

    const [editedOrderDate, setEditedOrderDate] = useState('');
    const [editedNote, setEditedNote] = useState('');
    const [editedTaxRate, setEditedTaxRate] = useState(0);
    const [editedIsVatIncluded, setEditedIsVatIncluded] = useState(false);
    const [editedIsItemBasedVat, setEditedIsItemBasedVat] = useState(false);
    const [editedCurrency, setEditedCurrency] = useState('TRY');
    const [editedShippingCost, setEditedShippingCost] = useState(0);
    const [editedDiscount, setEditedDiscount] = useState(0);

    const [shipmentItems, setShipmentItems] = useState({});
    const [cargoCompany, setCargoCompany] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [selectedShipments, setSelectedShipments] = useState([]);
    const [shipmentLoading, setShipmentLoading] = useState(false);

    const [pdfModalOpen, setPdfModalOpen] = useState(false);

    const [prepareOrderItems, setPrepareOrderItems] = useState({});

    const [returnItems, setReturnItems] = useState({});
    const [returnReason, setReturnReason] = useState('');
    const [returnNote, setReturnNote] = useState('');
    const [warehouses, setWarehouses] = useState([]);
    const [selectedReturnWarehouse, setSelectedReturnWarehouse] = useState('');
    const [activeTab, setActiveTab] = useState('summary'); // 'summary', 'shipments', 'payments', 'preparation', 'returns', 'activity'



    const navigateToOrders = () => navigate('/siparisler');

    const getProductName = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.name : 'Bilinmeyen Ürün';
    }, [products]);

    const getProductBarcode = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product ? (product.barcode || '-') : '-';
    }, [products]);

    const getProductStock = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product ? (product.quantity || 0) : 0;
    }, [products]);

    const getProductImage = useCallback((productId) => {
        const product = products.find(p => p.id === productId);
        return product?.imageUrl || null;
    }, [products]);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;

        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), (snapshot) => {
            setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubWarehouses = onSnapshot(query(collection(db, 'companies', companyId, 'warehouses'), where('isActive', '==', true)), (snapshot) => {
            setWarehouses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubProducts(); unsubWarehouses(); };
    }, [userData]);

    useEffect(() => {
        if (!userData?.companyId || !orderId) return;

        setLoading(true);
        const companyId = userData.companyId;

        const fetchCompanyInfo = async () => {
            const companyDocRef = doc(db, 'companies', companyId);
            const docSnap = await getDoc(companyDocRef);
            if (docSnap.exists()) setCompanyInfo(docSnap.data());
        };
        fetchCompanyInfo();

        const orderRef = doc(db, 'companies', companyId, 'orders', orderId);
        const unsubOrder = onSnapshot(orderRef, async (docSnap) => {
            if (docSnap.exists()) {
                let orderData = { id: docSnap.id, ...docSnap.data() };

                if (orderData.customerId && (!orderData.customerPhone || !orderData.customerEmail || !orderData.customerAddress || !orderData.customerCity || !orderData.customerDistrict)) {
                    try {
                        const customerRef = doc(db, 'companies', companyId, 'customers', orderData.customerId);
                        const customerSnap = await getDoc(customerRef);
                        if (customerSnap.exists()) {
                            const customerData = customerSnap.data();
                            orderData = {
                                ...orderData,
                                customerPhone: orderData.customerPhone || customerData.phone || '',
                                customerEmail: orderData.customerEmail || customerData.email || '',
                                customerAddress: orderData.customerAddress || customerData.address || '',
                                customerCity: orderData.customerCity || customerData.city || '',
                                customerDistrict: orderData.customerDistrict || customerData.district || ''
                            };
                        }
                    } catch (error) {
                        console.error('Error fetching customer data:', error);
                    }
                }

                setOrder(orderData);
                if (!isEditing) {
                    setEditedItems((orderData.lineItems || []).map(item => ({ ...item, name: getProductName(item.productId) })));
                    setEditedOrderDate(orderData.orderDate ? new Date(orderData.orderDate.seconds * 1000).toISOString().split('T')[0] : '');
                    setEditedTaxRate(orderData.vatRate !== undefined ? (orderData.vatRate * 100) : (orderData.taxRate || 0));
                    setEditedIsVatIncluded(orderData.isVatIncluded || false);
                    setEditedIsItemBasedVat(orderData.isItemBasedVat || false);
                    setEditedCurrency(orderData.currency || 'TRY');
                    setEditedNote(orderData.note || '');
                    setEditedShippingCost(orderData.shippingCost || 0);
                    setEditedDiscount(orderData.discount || 0);
                }
            }
            setLoading(false);
        });

        // Add real-time listener for customer data to update balance in real-time
        let unsubCustomer = () => { };
        if (orderId) {
            setTimeout(() => {
                getDoc(orderRef).then((orderSnap) => {
                    if (orderSnap.exists()) {
                        const orderData = orderSnap.data();
                        if (orderData.customerId) {
                            const customerRef = doc(db, 'companies', companyId, 'customers', orderData.customerId);
                            unsubCustomer = onSnapshot(customerRef, (customerSnap) => {
                                if (customerSnap.exists()) {
                                    const customerData = customerSnap.data();
                                    setOrder(prevOrder => {
                                        if (prevOrder) {
                                            return {
                                                ...prevOrder,
                                                customerBalance: customerData.balance || 0
                                            };
                                        }
                                        return prevOrder;
                                    });
                                }
                            });
                        }
                    }
                }).catch((error) => console.error('Error fetching order data:', error));
            }, 100);
        }

        const shipmentsQuery = query(collection(orderRef, 'shipments'), orderBy('shipmentDate', 'desc'));
        const unsubShipments = onSnapshot(shipmentsQuery, (snapshot) => setShipments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

        const paymentsQuery = query(collection(db, 'companies', companyId, 'transactions'), where('orderId', '==', orderId));
        const unsubPayments = onSnapshot(paymentsQuery, (snapshot) => {
            setPayments(snapshot.docs.map(doc => doc.data()));
        });

        return () => {
            unsubOrder();
            unsubShipments();
            unsubPayments();
            unsubCustomer();
        };
    }, [userData, orderId, isEditing, getProductName]);



    const shippedQuantities = useMemo(() => {
        const quantities = {};
        shipments.forEach(shipment => {
            (shipment.items || []).forEach(item => {
                quantities[item.productId] = (quantities[item.productId] || 0) + item.quantity;
            });
        });
        return quantities;
    }, [shipments]);

    const preparedQuantities = useMemo(() => {
        const quantities = {};
        if (order?.preparations) {
            order.preparations.forEach(preparation => {
                (preparation.items || []).forEach(item => {
                    quantities[item.productId] = (quantities[item.productId] || 0) + item.quantity;
                });
            });
        }
        return quantities;
    }, [order]);

    const handleCreateInvoice = async () => {
        if (!selectedShipments.length) return toast.error('Lütfen faturalaştırılacak sevkiyatları seçin.');
        const selectedShipmentObjects = shipments.filter(ship => selectedShipments.includes(ship.id));
        navigate('/faturalar/yeni', {
            state: {
                shipmentData: selectedShipmentObjects[0],
                orderId: orderId,
                multipleShipments: selectedShipmentObjects
            }
        });
    };

    const handleCombineShipmentsPDF = async () => {
        if (!selectedShipments.length) return toast.error('Lütfen yazdırılacak sevkiyatları seçin.');
        const selectedShipmentObjects = shipments.filter(ship => selectedShipments.includes(ship.id));
        try {
            await generateCombinedShipmentsPDF(companyInfo, order, selectedShipmentObjects, products);
            toast.success('Seçili irsaliyeler başarıyla PDF olarak oluşturuldu.');
        } catch (error) {
            console.error('PDF oluşturma hatası:', error);
            toast.error('PDF oluşturulurken bir hata oluştu.');
        }
    };

    const handleShipmentItemChange = (productId, value) => {
        const quantity = Math.max(0, Number(value));
        setShipmentItems(prev => ({ ...prev, [productId]: quantity }));
    };

    const handlePrepareOrderItemChange = (productId, value) => {
        if (!order) return;
        const originalItem = order.lineItems.find(item => item.productId === productId);
        if (!originalItem) return;
        const shipped = shippedQuantities[productId] || 0;
        const prepared = preparedQuantities[productId] || 0;
        const maxAllowed = originalItem.quantity - shipped - prepared;
        const quantity = Math.max(0, Math.min(Number(value), maxAllowed));
        setPrepareOrderItems(prev => ({ ...prev, [productId]: quantity }));
    };

    const handlePrepareOrder = async () => {
        if (!userData?.companyId || !order) return;
        const itemsToPrepare = Object.entries(prepareOrderItems)
            .map(([productId, quantity]) => {
                const originalItem = order.lineItems.find(item => item.productId === productId);
                return {
                    productId,
                    quantity,
                    name: getProductName(productId),
                    price: originalItem?.price || 0,
                    unit: products.find(p => p.id === productId)?.unit || 'Adet'
                };
            })
            .filter(item => item.quantity > 0);

        if (itemsToPrepare.length === 0) return toast.error("Lütfen hazırlanacak ürün miktarı girin.");

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        setActionLoading(true);

        try {
            for (const item of itemsToPrepare) {
                const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    const productData = productDoc.data();
                    const currentQuantity = productData.quantity || 0;
                    const blockedQuantity = productData.blockedQuantity || 0;
                    const newBlockedQuantity = blockedQuantity + item.quantity;
                    const availableQuantity = currentQuantity - blockedQuantity;

                    if (item.quantity > availableQuantity) {
                        toast.error(`"${item.name}" ürünü için yeterli stok yok. Mevcut: ${availableQuantity} ${item.unit}, İstenen: ${item.quantity} ${item.unit}`);
                        return;
                    }
                    batch.update(productRef, { blockedQuantity: newBlockedQuantity });
                    const movementRef = doc(collection(productRef, 'movements'));
                    batch.set(movementRef, {
                        date: serverTimestamp(),
                        type: `Sipariş Hazırlığı (Sipariş: ${orderId.substring(0, 6)})`,
                        quantity: `-${item.quantity} ${item.unit} (Blokaj)`,
                        orderId: orderId
                    });
                }
            }

            const orderRef = doc(db, 'companies', companyId, 'orders', orderId);
            const preparationRef = doc(collection(orderRef, 'preparations'));
            batch.set(preparationRef, {
                items: itemsToPrepare.map(item => ({ productId: item.productId, quantity: item.quantity, name: item.name, price: item.price })),
                preparationDate: serverTimestamp(),
                preparedBy: currentUser?.email || 'Bilinmeyen kullanıcı'
            });

            await batch.commit();
            setPrepareOrderItems({});
            toast.success("Ürünler başarıyla stokta bloke edildi!");
        } catch (error) {
            console.error("Sipariş hazırlama hatası:", error);
            toast.error("Sipariş hazırlanırken bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateShipment = async () => {
        const totalPaid = payments.reduce((acc, p) => acc + p.amount, 0);
        const remainingAmount = order.total - totalPaid;

        if (remainingAmount > 0) {
            if (!window.confirm(`Sipariş tam ödenmemiş! Yine de sevk etmek istiyor musunuz?`)) return;
        }

        const itemsToShip = Object.entries(shipmentItems)
            .map(([productId, quantity]) => {
                const originalItem = order.lineItems.find(item => item.productId === productId);
                return { productId, quantity, name: getProductName(productId), price: originalItem?.price || 0 };
            })
            .filter(item => item.quantity > 0);

        if (itemsToShip.length === 0) return toast.error("Lütfen sevkedilecek ürün miktarı girin."); // Replaced alert

        const batch = writeBatch(db);
        const companyId = userData.companyId;
        const orderRef = doc(db, 'companies', companyId, 'orders', orderId);
        const shipmentRef = doc(collection(orderRef, 'shipments'));
        setShipmentLoading(true); // Use specific loading state

        batch.set(shipmentRef, {
            items: itemsToShip,
            shipmentDate: serverTimestamp(),
            cargoCompany,
            trackingNumber,
            orderId: orderId,
            orderNumber: order.orderNumber || orderId.substring(0, 8),
            customerName: order.customerName,
            companyId: companyId,
            status: 'Gönderildi'
        });

        for (const item of itemsToShip) {
            const productRef = doc(db, 'companies', companyId, 'products', item.productId);
            const productDoc = await getDoc(productRef);
            const productData = productDoc.data();
            const currentBlocked = productData.blockedQuantity || 0;
            const newBlocked = Math.max(0, currentBlocked - item.quantity);
            const newQuantity = (productData.quantity || 0) - item.quantity;

            batch.update(productRef, { quantity: newQuantity, blockedQuantity: newBlocked });
            const movementRef = doc(collection(productRef, 'movements'));
            batch.set(movementRef, {
                date: serverTimestamp(),
                type: `Sevkiyat (Sipariş: ${orderId.substring(0, 6)})`,
                quantity: `-${item.quantity} ${productData.unit || 'Adet'}`
            });
        }

        const totalShipped = { ...shippedQuantities };
        itemsToShip.forEach(item => { totalShipped[item.productId] = (totalShipped[item.productId] || 0) + item.quantity; });
        const isFullyShipped = order.lineItems.every(item => totalShipped[item.productId] >= item.quantity);
        const newStatus = isFullyShipped ? 'Tamamlandı' : 'Kısmen Gönderildi';
        const oldStatus = order.status;

        batch.update(orderRef, { status: newStatus });

        try {
            await batch.commit();
            await logShipmentCreated(userData.companyId, orderId, currentUser, { items: itemsToShip, cargoCompany, trackingNumber, userRole: userData?.role || 'kullanıcı' });
            if (oldStatus !== newStatus) {
                await logOrderStatusChanged(userData.companyId, orderId, currentUser, { oldStatus, newStatus, reason: 'Sevkiyat oluşturma nedeniyle otomatik', userRole: userData?.role || 'kullanıcı' });
            }
            await createInvoiceForShipment(shipmentRef.id, itemsToShip, companyId, order);
            setShipmentItems({});
            setCargoCompany('');
            setTrackingNumber('');
            toast.success("Sevkiyat başarıyla oluşturuldu!");
        } catch (error) {
            console.error("Sevkiyat oluşturulurken hata:", error);
            toast.error("Sevkiyat oluşturulurken bir hata oluştu.");
        } finally {
            setShipmentLoading(false);
        }
    };

    const createInvoiceForShipment = async (shipmentId, itemsToShip, companyId, order) => {
        try {
            const batch = writeBatch(db);
            
            let calculatedSubTotal = 0;
            let finalVatAmount = 0;
            const globalVatRate = order.vatRate !== undefined ? order.vatRate : ((order.taxRate || 0) / 100);

            const orderSubTotal = order.subTotal || 1;
            const orderDiscountAmount = order.discount || order.discountAmount || 0;
            const discountPercentage = orderSubTotal > 0 ? (orderDiscountAmount / orderSubTotal) : 0;

            itemsToShip.forEach(item => {
                const originalItem = order.lineItems.find(i => i.productId === item.productId) || {};
                const itemVatRate = order.isItemBasedVat ? (originalItem.vatRate || 0) : globalVatRate;
                const qty = item.quantity || 1;
                const enteredPrice = item.price || 0;
                
                let unitBasePrice = enteredPrice;
                if (order.isVatIncluded) {
                    unitBasePrice = enteredPrice / (1 + itemVatRate);
                }

                const lineBaseTotal = unitBasePrice * qty;
                calculatedSubTotal += lineBaseTotal;
                
                const discountedLineBaseTotal = lineBaseTotal * (1 - discountPercentage);
                finalVatAmount += discountedLineBaseTotal * itemVatRate;
            });

            const shipmentDiscountAmount = calculatedSubTotal * discountPercentage;
            const subTotal = calculatedSubTotal;
            const vatAmount = finalVatAmount;
            const total = (subTotal - shipmentDiscountAmount) + vatAmount;

            const invoiceRef = doc(collection(db, 'companies', companyId, 'invoices'));
            batch.set(invoiceRef, {
                customerId: order.customerId,
                customerName: order.customerName,
                invoiceDate: serverTimestamp(),
                orderId: orderId,
                lineItems: itemsToShip.map(item => {
                    const originalItem = order.lineItems.find(i => i.productId === item.productId) || {};
                    return {
                        ...item,
                        vatRate: order.isItemBasedVat ? (originalItem.vatRate || 0) : globalVatRate
                    };
                }),
                subTotal,
                discountAmount: shipmentDiscountAmount,
                vatAmount,
                total,
                currency: order.currency,
                isVatIncluded: order.isVatIncluded || false,
                isItemBasedVat: order.isItemBasedVat || false,
                status: 'Faturalaştırıldı'
            });

            const shipmentRef = doc(db, 'companies', companyId, 'orders', orderId, 'shipments', shipmentId);
            batch.update(shipmentRef, { status: 'Faturalandı', invoiceId: invoiceRef.id });

            const customerRef = doc(db, 'companies', companyId, 'customers', order.customerId);
            const customerDoc = await getDoc(customerRef);
            if (!customerDoc.exists()) throw new Error('Müşteri bulunamadı');

            const customerData = customerDoc.data();
            const currentBalance = customerData.balance || 0;
            let invoiceAmount = total;
            if (customerData.currency !== order.currency) {
                if (order.currency === 'USD' && customerData.currency === 'TRY') invoiceAmount = total * (order.exchangeRate || 1);
                else if (order.currency === 'TRY' && customerData.currency === 'USD') invoiceAmount = total / (order.exchangeRate || 1);
            }

            const newBalance = currentBalance - invoiceAmount;
            batch.update(customerRef, { balance: newBalance });

            const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
            batch.set(balanceHistoryRef, {
                date: serverTimestamp(),
                type: 'Fatura',
                description: `Sipariş #${orderId.substring(0, 6)} için otomatik fatura`,
                amount: invoiceAmount,
                previousBalance: currentBalance,
                newBalance: newBalance,
                orderId: orderId,
                invoiceId: invoiceRef.id
            });

            await batch.commit();
        } catch (error) {
            console.error("Otomatik fatura hatası:", error);
        }
    };

    const handleToggleShipmentSelection = (shipmentId) => {
        setSelectedShipments(prev => prev.includes(shipmentId) ? prev.filter(id => id !== shipmentId) : [...prev, shipmentId]);
    };
    const handleSelectAllShipments = (checked) => {
        const selectableShipments = shipments.filter(s => s.status !== 'Faturalandı');
        setSelectedShipments(checked ? selectableShipments.map(s => s.id) : []);
    };

    const handleExportDetail = () => {
        if (!order) return;
        const currencySymbol = order.currency === 'USD' ? '$' : '₺';
        const orderInfo = [
            { 'Bilgi': 'Sipariş No', 'Değer': order.id.substring(0, 6) },
            { 'Bilgi': 'Müşteri', 'Değer': order.customerName },
            { 'Bilgi': 'Tarih', 'Değer': new Date(order.orderDate.seconds * 1000).toLocaleDateString() },
            { 'Bilgi': `Toplam Tutar (${currencySymbol})`, 'Değer': order.total.toFixed(2) },
            { 'Bilgi': 'Durum', 'Değer': order.status },
        ];
        const orderItems = order.lineItems.map(item => ({
            'Stok Kodu': getProductBarcode(item.productId),
            'Ürün Adı': getProductName(item.productId),
            'Sipariş Miktarı': item.quantity,
            [`Birim Fiyat (${currencySymbol})`]: item.price,
            [`Toplam (${currencySymbol})`]: item.total
        }));
        const ws1 = XLSX.utils.json_to_sheet(orderInfo, { skipHeader: true });
        XLSX.utils.sheet_add_json(ws1, orderItems, { origin: 'A7', header: ['Stok Kodu', 'Ürün Adı', 'Sipariş Miktarı', `Birim Fiyat (${currencySymbol})`, `Toplam (${currencySymbol})`] });
        const ws2 = XLSX.utils.json_to_sheet(shipments.map(ship => ({
            'Sevkiyat Tarihi': ship.shipmentDate ? new Date(ship.shipmentDate.seconds * 1000).toLocaleDateString() : 'Bilinmiyor',
            'Ürünler': ship.items.map(i => `${i.name} (${i.quantity} adet)`).join(', '),
            'Kargo Firması': ship.cargoCompany || '-',
            'Takip Numarası': ship.trackingNumber || '-',
            'Durum': ship.status || 'Gönderildi'
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, ws1, "Sipariş Detayı");
        XLSX.utils.book_append_sheet(workbook, ws2, "Sevkiyatlar");
        XLSX.writeFile(workbook, generateOrderFilename(order, 'xlsx', 'SiparisDetay'));
    };

    const handleReceivePayment = async (e) => {
        e.preventDefault();
        const amount = Number(paymentAmount);
        if (!amount || amount <= 0) return toast.error("Lütfen geçerli bir tutar girin.");

        setActionLoading(true);
        const batch = writeBatch(db);
        const companyId = userData.companyId;
        const transactionId = `txn_${Date.now()}`;

        const transactionRef = doc(collection(db, 'companies', companyId, 'transactions'));
        batch.set(transactionRef, {
            type: 'Tahsilat', amount, currency: order.currency, date: serverTimestamp(),
            description: `Sipariş #${order.id.substring(0, 6)} için ödeme`,
            orderId: order.id, relatedPartyId: order.customerId, relatedPartyName: order.customerName, transactionId
        });

        const customerRef = doc(db, 'companies', companyId, 'customers', order.customerId);
        const customerSnap = await getDoc(customerRef);
        const customerData = customerSnap.data();
        const currentBalance = customerData.balance || 0;

        let convertedAmount = amount;
        if (customerData.currency !== order.currency) {
            if (order.currency === 'USD' && customerData.currency === 'TRY') convertedAmount = amount * (order.exchangeRate || 1);
            else if (order.currency === 'TRY' && customerData.currency === 'USD') convertedAmount = amount / (order.exchangeRate || 1);
        }

        const newBalance = currentBalance + convertedAmount;
        batch.update(customerRef, { balance: newBalance, lastPaymentDate: serverTimestamp() });

        const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
        batch.set(balanceHistoryRef, {
            date: serverTimestamp(), type: 'Tahsilat', description: `Sipariş #${order.id.substring(0, 6)} için ödeme`,
            amount: convertedAmount, previousBalance: currentBalance, newBalance, orderId, transactionId
        });

        try {
            await batch.commit();
            await logPaymentReceived(userData.companyId, orderId, currentUser, { amount, currency: order.currency, method: 'Ödeme', customerName: order.customerName, transactionId, userRole: userData?.role || 'kullanıcı' });
            setPaymentAmount('');
            toast.success("Ödeme başarıyla kaydedildi!");
        } catch (error) {
            console.error("Ödeme hatası:", error);
            toast.error("Ödeme kaydedilirken bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleStartEdit = () => {
        setIsEditing(true);
        if (order) {
            setEditedOrderDate(order.orderDate?.seconds ? new Date(order.orderDate.seconds * 1000).toISOString().split('T')[0] : '');
            setEditedNote(order.note || '');
            setEditedTaxRate(order.vatRate !== undefined ? (order.vatRate * 100) : (order.taxRate || 0));
            setEditedIsVatIncluded(order.isVatIncluded || false);
            setEditedIsItemBasedVat(order.isItemBasedVat || false);
            setEditedCurrency(order.currency || 'TRY');
            setEditedShippingCost(order.shippingCost || 0);
            setEditedDiscount(order.discount || 0);
        }
    };
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedItems(order.lineItems.map(item => ({ ...item, name: getProductName(item.productId) })));
    };

    const handleUpdateItem = (index, field, value) => {
        const newItems = [...editedItems];
        newItems[index][field] = Number(value);
        newItems[index].total = newItems[index].quantity * newItems[index].price;
        setEditedItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...editedItems];
        newItems.splice(index, 1);
        setEditedItems(newItems);
    };

    const handleProductSelectForNewItem = (product) => {
        setNewItem({ productId: product.id, productName: product.name, quantity: 1, price: product.salePrice || 0, vatRate: newItem.vatRate !== undefined ? newItem.vatRate : 0.20, imageUrl: product.imageUrl || '', imageData: null });
    };

    const handleAddNewItem = () => {
        if ((!newItem.productId && !newItem.productName.trim()) || newItem.quantity <= 0) return;
        const isCustomProduct = !newItem.productId;
        const effectiveProductId = newItem.productId || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setEditedItems([...editedItems, { ...newItem, productId: effectiveProductId, isNewCustomProduct: isCustomProduct, total: newItem.quantity * newItem.price, name: newItem.productName, imageUrl: newItem.imageUrl, imageData: newItem.imageData }]);
        setNewItem({ productId: '', productName: '', quantity: 1, price: 0, vatRate: newItem.vatRate !== undefined ? newItem.vatRate : 0.20, imageUrl: '', imageData: null });
    };

    const handleSaveOrder = async () => {
        const orderRef = doc(db, 'companies', userData.companyId, 'orders', orderId);
        
        let calculatedSubTotal = 0;
        let finalVatAmount = 0;
        const globalVatRate = Number(editedTaxRate) / 100;

        editedItems.forEach(item => {
            const itemVatRate = editedIsItemBasedVat ? (item.vatRate || 0) : globalVatRate;
            const qty = item.quantity || 1;
            const enteredPrice = item.price || 0;
            
            let unitBasePrice = enteredPrice;
            if (editedIsVatIncluded) {
                unitBasePrice = enteredPrice / (1 + itemVatRate);
            }

            const lineBaseTotal = unitBasePrice * qty;
            calculatedSubTotal += lineBaseTotal;
        });

        const discountAmount = Number(editedDiscount);
        const discountPercentage = calculatedSubTotal > 0 ? (discountAmount / calculatedSubTotal) : 0;

        editedItems.forEach(item => {
            const itemVatRate = editedIsItemBasedVat ? (item.vatRate || 0) : globalVatRate;
            const qty = item.quantity || 1;
            const enteredPrice = item.price || 0;
            
            let unitBasePrice = enteredPrice;
            if (editedIsVatIncluded) {
                unitBasePrice = enteredPrice / (1 + itemVatRate);
            }

            const lineBaseTotal = unitBasePrice * qty;
            const discountedLineBaseTotal = lineBaseTotal * (1 - discountPercentage);
            finalVatAmount += discountedLineBaseTotal * itemVatRate;
        });

        const subTotal = calculatedSubTotal;
        const vatAmount = finalVatAmount;
        const total = (subTotal - discountAmount) + vatAmount + Number(editedShippingCost);

        const itemChanges = { updated: [], userRole: userData?.role || 'kullanıcı' };
        editedItems.forEach((newItem, index) => {
            const originalItem = order.lineItems[index];
            if (originalItem && (originalItem.quantity !== newItem.quantity || originalItem.price !== newItem.price)) {
                itemChanges.updated.push({ name: editedItems[index].name, changes: { quantity: originalItem.quantity !== newItem.quantity ? `${originalItem.quantity} → ${newItem.quantity}` : undefined, price: originalItem.price !== newItem.price ? `${originalItem.price} → ${newItem.price}` : undefined } });
            }
        });

        try {
            const batch = writeBatch(db);

            // Create custom products if any exist in the edited items
            const customProductsToCreate = editedItems.filter(item => item.isNewCustomProduct);
            if (customProductsToCreate.length > 0) {
                const groupsRef = collection(db, 'companies', userData.companyId, 'productGroups');
                const groupsSnap = await getDocs(query(groupsRef, where("name", "==", "Özel Siparişler")));
                let specialGroupId = '';
                if (groupsSnap.empty) {
                    const newGroupRef = doc(groupsRef);
                    batch.set(newGroupRef, { name: "Özel Siparişler", createdAt: serverTimestamp() });
                    specialGroupId = newGroupRef.id;
                } else {
                    specialGroupId = groupsSnap.docs[0].id;
                }

                customProductsToCreate.forEach(item => {
                    const productRef = doc(db, 'companies', userData.companyId, 'products', item.productId);
                    batch.set(productRef, {
                        name: item.name,
                        barcode: '',
                        groupId: specialGroupId,
                        quantity: 0,
                        unit: 'Adet',
                        salePrice: item.price,
                        criticalLevel: 10,
                        warehouseQuantities: {},
                        createdAt: serverTimestamp(),
                        isCustomOrderProduct: true,
                        reservedForCustomer: order.customerName,
                        reservedForOrderId: orderId,
                        imageUrl: item.imageUrl || '',
                        imageData: item.imageData || null
                    });

                    const movementRef = doc(collection(productRef, 'movements'));
                    batch.set(movementRef, {
                        date: serverTimestamp(),
                        type: 'Sipariş Üzerinden (Özel)',
                        quantity: '0 Adet',
                        description: `Sipariş: ${orderId.substring(0, 6).toUpperCase()} ile müşteri için özel oluşturuldu.`
                    });
                });
            }

            const cleanItems = editedItems.map(({ name, isNewCustomProduct, imageData, ...rest }) => rest);
            batch.update(orderRef, { 
                lineItems: cleanItems, 
                subTotal, 
                vatAmount, 
                total, 
                taxRate: Number(editedTaxRate), 
                vatRate: globalVatRate,
                isVatIncluded: editedIsVatIncluded,
                isItemBasedVat: editedIsItemBasedVat,
                currency: editedCurrency,
                shippingCost: Number(editedShippingCost), 
                discount: Number(editedDiscount), 
                note: editedNote, 
                orderDate: editedOrderDate ? new Date(editedOrderDate) : order.orderDate 
            });
            await batch.commit();
            await logOrderUpdated(userData.companyId, orderId, currentUser, { subTotal: `${order.subTotal?.toFixed(2)} → ${subTotal.toFixed(2)}`, total: `${order.total?.toFixed(2)} → ${total.toFixed(2)}`, userRole: userData?.role || 'kullanıcı' });
            if (itemChanges.updated.length > 0) await logItemsChanged(userData.companyId, orderId, currentUser, itemChanges);
            setIsEditing(false);
        } catch (error) {
            console.error("Güncelleme hatası:", error);
        }
    };

    const handleDeleteOrder = async () => {
        if (window.confirm("Bu siparişi silmek istediğinizden emin misiniz?")) {
            const orderRef = doc(db, 'companies', userData.companyId, 'orders', orderId);
            try {
                const batch = writeBatch(db);
                
                // Fetch lineItems to see if any are custom products
                const customProductsToDelete = order.lineItems.filter(item => item.productId && item.productId.startsWith('custom_'));
                for (const item of customProductsToDelete) {
                    const productRef = doc(db, 'companies', userData.companyId, 'products', item.productId);
                    // verify if it is indeed a custom order product
                    const pDoc = await getDoc(productRef);
                    if (pDoc.exists() && pDoc.data().isCustomOrderProduct) {
                        batch.delete(productRef);
                    }
                }

                await logOrderDeleted(userData.companyId, orderId, currentUser, { customerName: order.customerName, total: order.total, currency: order.currency, deleteReason: 'Kullanıcı talebi', userRole: userData?.role || 'kullanıcı' }, batch);
                batch.delete(orderRef);
                await batch.commit();
                navigateToOrders();
            } catch (error) {
                console.error("Silme hatası:", error);
            }
        }
    };

    const handleCloseOrder = async () => {
        if (!order || !window.confirm("Bu siparişi manuel olarak 'Tamamlandı' durumuna getirmek istediğinizden emin misiniz?")) return;
        const orderRef = doc(db, 'companies', userData.companyId, 'orders', orderId);
        try {
            const batch = writeBatch(db);
            batch.update(orderRef, { status: 'Tamamlandı' });
            await batch.commit();
            await logOrderStatusChanged(userData.companyId, orderId, currentUser, { oldStatus: order.status, newStatus: 'Tamamlandı', reason: 'Manuel kapatma', userRole: userData?.role || 'kullanıcı' });
        } catch (error) {
            console.error("Kapatma hatası:", error);
        }
    };

    const handleRepeatOrder = () => {
        navigate('/siparisler/yeni', { state: { repeatedOrder: order } });
    };

    const handleReturn = async () => {
        const itemsToReturn = Object.entries(returnItems).filter(([_, qty]) => qty > 0).map(([id, qty]) => {
            const orig = order.lineItems.find(i => i.productId === id);
            return { productId: id, quantity: parseInt(qty), name: getProductName(id), price: orig?.price || 0, unit: products.find(p => p.id === id)?.unit || 'Adet' };
        });
        if (itemsToReturn.length === 0 || !returnReason) return toast.error("Lütfen iade nedeni ve ürün miktarı seçin.");

        if (!window.confirm("İadeyi onaylıyor musunuz?")) return;

        setActionLoading(true);
        const companyId = userData.companyId;
        const batch = writeBatch(db);

        try {
            for (const item of itemsToReturn) {
                const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                const productDoc = await getDoc(productRef);
                if (productDoc.exists()) {
                    const currentData = productDoc.data();
                    let updates = { quantity: (currentData.quantity || 0) + item.quantity };
                    
                    if (selectedReturnWarehouse) {
                        const currentWQ = currentData.warehouseQuantities || {};
                        updates.warehouseQuantities = {
                            ...currentWQ,
                            [selectedReturnWarehouse]: (currentWQ[selectedReturnWarehouse] || 0) + item.quantity
                        };
                    }
                    
                    batch.update(productRef, updates);
                    const movRef = doc(collection(productRef, 'movements'));
                    batch.set(movRef, { date: serverTimestamp(), type: `İade (Sipariş: ${orderId.substring(0, 6)})`, quantity: `+${item.quantity} ${item.unit}`, orderId, returnReason, warehouseId: selectedReturnWarehouse });
                }
            }

            const returnRef = doc(collection(db, 'companies', companyId, 'returns'));
            const refundTotal = itemsToReturn.reduce((s, i) => s + (i.price * i.quantity), 0);
            batch.set(returnRef, { orderId, customerId: order.customerId, customerName: order.customerName, items: itemsToReturn, reason: returnReason, note: returnNote, returnDate: serverTimestamp(), createdBy: currentUser?.email, totalAmount: refundTotal, currency: order.currency, type: 'İade' });

            const newRetItems = { ...(order.returnedItems || {}) };
            itemsToReturn.forEach(i => newRetItems[i.productId] = (newRetItems[i.productId] || 0) + i.quantity);

            batch.update(doc(db, 'companies', companyId, 'orders', orderId), { returnedItems: newRetItems, returnCount: (order.returnCount || 0) + 1 });

            // Refund logic
            const custRef = doc(db, 'companies', companyId, 'customers', order.customerId);
            const custDoc = await getDoc(custRef);
            if (custDoc.exists()) {
                const custData = custDoc.data();
                let convertedRefund = refundTotal;
                if (custData.currency !== order.currency) {
                    if (order.currency === 'USD' && custData.currency === 'TRY') convertedRefund = refundTotal * (order.exchangeRate || 1);
                    else if (order.currency === 'TRY' && custData.currency === 'USD') convertedRefund = refundTotal / (order.exchangeRate || 1);
                }
                batch.update(custRef, { balance: (custData.balance || 0) + convertedRefund });
                batch.set(doc(collection(custRef, 'balanceHistory')), { date: serverTimestamp(), type: 'İade', amount: convertedRefund, newBalance: (custData.balance || 0) + convertedRefund, orderId, refundId: returnRef.id });
            }

            await batch.commit();
            setReturnItems({}); setReturnReason(''); setReturnNote('');
            toast.success("İade başarıyla tamamlandı.");
        } catch (error) {
            console.error("İade hatası:", error);
            toast.error("İade sırasında bir hata oluştu.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleExportRemainingItemsPDF = () => {
        try {
            const doc = new jsPDF();
            if (font) { doc.addFileToVFS("Roboto-Regular.ttf", font); doc.addFont("Roboto-Regular.ttf", "Roboto", "normal"); doc.setFont("Roboto"); }
            doc.setFontSize(18); doc.text("Kalan Ürünler Listesi", 14, 20);
            const tableData = order.lineItems.map(item => {
                const rem = item.quantity - (shippedQuantities[item.productId] || 0) - (preparedQuantities[item.productId] || 0);
                return rem > 0 ? [getProductBarcode(item.productId), getProductName(item.productId), item.quantity, (shippedQuantities[item.productId] || 0), rem] : null;
            }).filter(Boolean);
            if (tableData.length === 0) return toast.error("Kalan ürün yok.");
            autoTable(doc, { startY: 45, head: [['Barkod', 'Ürün Adı', 'Sipariş', 'Gönderilen', 'Kalan']], body: tableData, styles: { font: "Roboto" } });
            doc.save(`Kalan_Urunler_${order.orderNumber || orderId}.pdf`);
        } catch (error) { console.error(error); }
    };

    const totalOrderQuantity = order?.lineItems?.reduce((acc, item) => acc + item.quantity, 0) || 0;
    const totalShippedQuantity = Object.values(shippedQuantities).reduce((acc, val) => acc + val, 0) || 0;
    const totalRemainingQuantity = Math.max(0, totalOrderQuantity - totalShippedQuantity);

    if (loading) return <div style={styles.container}><Link to="/siparisler" style={{ textDecoration: 'none', color: '#06C167' }}>← Dön</Link><div>Yükleniyor...</div></div>;

    return (
        <div style={styles.container}>
            <style>{mobileStyles}</style>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <style>
                {`
                    .order-detail-container { padding: 32px; background: #f8fafc; border-radius: 24px; }
                    .detail-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; border-bottom: 1px solid #e2e8f0; padding-bottom: 24px; }
                    .detail-title { font-size: 28px; font-weight: 800; color: #0f172a; margin: 0; }
                    .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-bottom: 32px; }
                    .info-card { background: white; padding: 32px; border-radius: 24px; border: 1px solid #e2e8f0; }
                    .responsive-table-wrapper { overflow-x: auto; background: white; border-radius: 16px; border: 1px solid #e2e8f0; }
                    .customer-link:hover { color: #4f46e5; text-decoration: underline; }
                    @media (max-width: 1024px) { .detail-grid { grid-template-columns: 1fr; } }
                `}
            </style>
            
            <div className="order-detail-container">
                {/* Breadcrumb / Back button */}
                <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                    <span onClick={navigateToOrders} style={{ cursor: 'pointer', hover: { color: '#4f46e5' }, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        📁 Siparişler
                    </span>
                    <span>/</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        #{order.orderNumber || order.id.substring(0, 8)}
                    </span>
                </div>

                <div className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap', gap: '20px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: '#1e293b', letterSpacing: '-0.02em' }}>
                                Sipariş Detayı
                            </h1>
                            <div style={styles.statusBadge(order.status)}>
                                {order.status === 'Tamamlandı' ? '✅' : 
                                 order.status === 'İptal Edildi' ? '❌' : 
                                 order.status === 'Beklemede' ? '⏳' : '📦'} {order.status}
                            </div>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                            <span style={{ fontWeight: '600' }}>Olusturulma:</span> {order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleString() : '-'}
                        </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {isEditing ? (
                            <>
                                <button onClick={handleCancelEdit} style={{ ...styles.button, background: '#94a3b8' }}>
                                    İptal
                                </button>
                                <button onClick={handleSaveOrder} style={{ ...styles.button, background: '#10b981', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}>
                                    💾 Kaydet
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={handleStartEdit} style={styles.button}>
                                    ✏️ Düzenle
                                </button>
                                <button onClick={handleRepeatOrder} style={{ ...styles.button, background: '#3b82f6', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)' }}>
                                    🔁 Siparişi Tekrarla
                                </button>
                                <button onClick={() => setPdfModalOpen(true)} style={styles.exportButton}>
                                    📄 PDF
                                </button>
                                <button onClick={handleExportDetail} style={styles.exportButton}>
                                    📊 Excel
                                </button>
                                <UsageLimitGuard type="orders">
                                    <button onClick={handleCloseOrder} style={{ ...styles.button, background: '#f59e0b', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)' }}>
                                        🏁 Kapat
                                    </button>
                                </UsageLimitGuard>
                                <button onClick={handleDeleteOrder} style={{ ...styles.button, background: '#ef4444', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)' }}>
                                    🗑️ Sil
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Tab Navigation */}
                <div style={styles.tabBar}>
                    {[
                        { id: 'summary', label: '📋 Özet' },
                        { id: 'shipments', label: '📦 Sevkiyatlar' },
                        { id: 'payments', label: '💰 Ödemeler' },
                        { id: 'preparation', label: '🏗️ Hazırlık' },
                        { id: 'returns', label: '🔄 İade/Değişim' },
                        { id: 'activity', label: '⏳ Hareketler' }
                    ].map(tab => (
                        <div 
                            key={tab.id}
                            style={{
                                ...styles.tab,
                                ...(activeTab === tab.id ? styles.activeTab : {})
                            }}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'summary' && (
                    <>
                        <div style={styles.gridTwo}>
                            <div style={styles.infoCard}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                                    <div style={{ background: '#eef2ff', padding: '10px', borderRadius: '12px' }}>👤</div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Müşteri Bilgileri</h3>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Müşteri</span>
                                        <Link to={`/musteriler/${order.customerId}`} style={styles.customerLink}>{order.customerName}</Link>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Telefon</span>
                                        <span style={{ fontWeight: '600', color: '#334155' }}>{order.customerPhone || '-'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>E-posta</span>
                                        <span style={{ fontWeight: '600', color: '#334155' }}>{order.customerEmail || '-'}</span>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Adres</span>
                                        <span style={{ fontWeight: '500', color: '#334155', lineHeight: '1.6', fontSize: '14px' }}>
                                            {order.customerAddress || '-'} {order.customerDistrict} / {order.customerCity}
                                        </span>
                                    </div>
                                    <div style={{ marginTop: '8px', padding: '16px', background: (order.customerBalance || 0) >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${(order.customerBalance || 0) >= 0 ? '#dcfce7' : '#fee2e2'}` }}>
                                        <span style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>Cari Bakiye</span>
                                        <span style={{ color: (order.customerBalance || 0) >= 0 ? '#10b981' : '#ef4444', fontWeight: '800', fontSize: '18px' }}>
                                            {(order.customerBalance || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div style={styles.infoCard}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                                    <div style={{ background: '#fff7ed', padding: '10px', borderRadius: '12px' }}>📋</div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Sipariş Özeti</h3>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Tarih</span>
                                        {isEditing ? (
                                            <input type="date" style={{ ...styles.input, width: 'auto', padding: '8px 12px' }} value={editedOrderDate} onChange={e => setEditedOrderDate(e.target.value)} />
                                        ) : (
                                            <span style={{ fontWeight: '600', color: '#334155' }}>{order.orderDate ? new Date(order.orderDate.seconds * 1000).toLocaleDateString() : '-'}</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Para Birimi</span>
                                        {isEditing ? (
                                            <select style={{ ...styles.input, width: 'auto', padding: '8px 12px' }} value={editedCurrency} onChange={e => setEditedCurrency(e.target.value)}>
                                                <option value="TRY">₺ (TL)</option>
                                                <option value="USD">$ (USD)</option>
                                            </select>
                                        ) : (
                                            <span style={{ fontWeight: '700', color: '#4f46e5' }}>{order.currency || 'TRY'}</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>KDV Durumu</span>
                                        {isEditing ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <input type="checkbox" checked={editedIsVatIncluded} onChange={e => setEditedIsVatIncluded(e.target.checked)} /> Fiyatlara KDV Dahil
                                                </label>
                                                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <input type="checkbox" checked={editedIsItemBasedVat} onChange={e => setEditedIsItemBasedVat(e.target.checked)} /> Ürün Bazlı KDV Uygula
                                                </label>
                                                {!editedIsItemBasedVat && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>Genel KDV:</span>
                                                        <select style={{ ...styles.input, width: '80px', padding: '4px 8px' }} value={editedTaxRate} onChange={e => setEditedTaxRate(e.target.value)}>
                                                            <option value="0">%0</option>
                                                            <option value="1">%1</option>
                                                            <option value="10">%10</option>
                                                            <option value="20">%20</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                <span style={{ fontWeight: '600', color: '#334155' }}>
                                                    {order.isItemBasedVat ? 'Ürün Bazlı KDV' : `Genel KDV (%${order.taxRate || 0})`}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                    {order.isVatIncluded ? 'Fiyatlara KDV Dahil' : 'Fiyatlara KDV Hariç'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Ödeme Yöntemi</span>
                                        <span style={{ fontWeight: '600', color: '#334155' }}>{order.paymentMethod || 'Belirtilmedi'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', marginTop: '4px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Toplam Sipariş Edilen</span>
                                        <span style={{ fontWeight: '600', color: '#334155' }}>{totalOrderQuantity} Adet</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Gönderilen Ürün</span>
                                        <span style={{ fontWeight: '600', color: '#10b981' }}>{totalShippedQuantity} Adet</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px' }}>Kalan Ürün</span>
                                        <span style={{ fontWeight: '600', color: totalRemainingQuantity > 0 ? '#f59e0b' : '#10b981' }}>{totalRemainingQuantity} Adet</span>
                                    </div>
                                    <div style={{ marginTop: '8px', padding: '16px', background: '#f8fafc', borderRadius: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px dashed #cbd5e1' }}>
                                        <span style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>Toplam Tutar</span>
                                        <span style={{ color: '#1e293b', fontWeight: '800', fontSize: '20px' }}>
                                            {(order.total || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ ...styles.infoCard, padding: '0', overflow: 'hidden', marginTop: '32px' }}>
                            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.5)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '12px' }}>🛍️</div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Ürünler</h3>
                                </div>
                                <button onClick={handleExportRemainingItemsPDF} style={styles.secondaryButton}>📄 Kalan Ürünler (PDF)</button>
                            </div>
                            <div className="responsive-table-wrapper">
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Görsel</th>
                                            <th style={styles.th}>Ürün</th>
                                            <th style={styles.th}>Barkod</th>
                                            <th style={styles.th}>Miktar</th>
                                            {!isEditing && <th style={styles.th}>Hazırlanan</th>}
                                            {!isEditing && <th style={styles.th}>Sevk Edilen</th>}
                                            <th style={styles.th}>Birim Fiyat</th>
                                            {((isEditing && editedIsItemBasedVat) || (!isEditing && order.isItemBasedVat)) && <th style={styles.th}>KDV</th>}
                                            <th style={styles.th}>Toplam</th>
                                            {isEditing && <th style={styles.th}>İşlem</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isEditing ? (
                                            <>
                                                {editedItems.map((item, index) => {
                                                    const img = getProductImage(item.productId);
                                                    const barcode = getProductBarcode(item.productId);
                                                    return (
                                                        <tr key={index}>
                                                            <td style={styles.td}>
                                                                {img || item.imageUrl ? <img src={img || item.imageUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '4px' }} />}
                                                            </td>
                                                            <td style={styles.td}>
                                                                {item.name || getProductName(item.productId)}
                                                                {item.isNewCustomProduct && (
                                                                    <div style={{ fontSize: '11px', color: '#d97706', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>Özel Sipariş / Çizim</div>
                                                                )}
                                                            </td>
                                                            <td style={styles.td}>{barcode}</td>
                                                            <td style={styles.td}>
                                                                <input type="number" step="any" style={{ ...styles.input, width: '80px' }} value={item.quantity} onChange={e => handleUpdateItem(index, 'quantity', e.target.value)} />
                                                            </td>
                                                            <td style={styles.td}>
                                                                <input type="number" step="any" style={{ ...styles.input, width: '100px' }} value={item.price} onChange={e => handleUpdateItem(index, 'price', e.target.value)} />
                                                            </td>
                                                            {editedIsItemBasedVat && (
                                                                <td style={styles.td}>
                                                                    <select
                                                                        style={{ ...styles.input, width: '70px', padding: '6px' }}
                                                                        value={Math.round((item.vatRate || 0) * 100)}
                                                                        onChange={e => handleUpdateItem(index, 'vatRate', Number(e.target.value) / 100)}
                                                                    >
                                                                        <option value="0">%0</option>
                                                                        <option value="1">%1</option>
                                                                        <option value="10">%10</option>
                                                                        <option value="20">%20</option>
                                                                    </select>
                                                                </td>
                                                            )}
                                                            <td style={styles.td}>{(item.quantity * item.price).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</td>
                                                            <td style={styles.td}>
                                                                <button onClick={() => handleRemoveItem(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}>Sil</button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {/* Add New Item Row */}
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <td style={styles.td}>
                                                        {newItem.productId && getProductImage(newItem.productId) && <img src={getProductImage(newItem.productId)} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />}
                                                    </td>
                                                    <td style={styles.td}>
                                                        <div style={{ position: 'relative' }}>
                                                            <input 
                                                                placeholder="Ürün Ara..." 
                                                                style={styles.input} 
                                                                value={newItem.productName} 
                                                                onChange={e => {
                                                                    setNewItem({ ...newItem, productName: e.target.value });
                                                                    const filtered = products.filter(p => p.name.toLowerCase().includes(e.target.value.toLowerCase())).slice(0, 5);
                                                                    setProductSuggestions(filtered);
                                                                }} 
                                                                onFocus={() => {
                                                                    if (newItem.productName) {
                                                                        const filtered = products.filter(p => p.name.toLowerCase().includes(newItem.productName.toLowerCase())).slice(0, 5);
                                                                        setProductSuggestions(filtered);
                                                                    }
                                                                }}
                                                            />
                                                            {productSuggestions.length > 0 && (
                                                                <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                                                                    {productSuggestions.map(p => (
                                                                        <div 
                                                                            key={p.id} 
                                                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                                                                            onClick={() => {
                                                                                handleProductSelectForNewItem(p);
                                                                                setProductSuggestions([]);
                                                                            }}
                                                                        >
                                                                            {p.name} ({p.salePrice} {order.currency})
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {!newItem.productId && newItem.productName && (
                                                            <div style={{ marginTop: '8px' }}>
                                                                <ImageUpload 
                                                                    onImageUpload={(imgData) => setNewItem(prev => ({ ...prev, imageUrl: imgData ? imgData.thumbnailUrl : '', imageData: imgData }))}
                                                                    currentImageUrl={newItem.imageUrl}
                                                                    label="Çizim Yükle"
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={styles.td}>{newItem.productId ? getProductBarcode(newItem.productId) : '-'}</td>
                                                    <td style={styles.td}>
                                                        <input type="number" step="any" style={{ ...styles.input, width: '80px' }} value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: Number(e.target.value) })} />
                                                    </td>
                                                    <td style={styles.td}>
                                                        <input type="number" step="any" style={{ ...styles.input, width: '100px' }} value={newItem.price} onChange={e => setNewItem({ ...newItem, price: Number(e.target.value) })} />
                                                    </td>
                                                    {editedIsItemBasedVat && (
                                                        <td style={styles.td}>
                                                            <select
                                                                style={{ ...styles.input, width: '70px', padding: '6px' }}
                                                                value={Math.round((newItem.vatRate || 0.20) * 100)}
                                                                onChange={e => setNewItem({ ...newItem, vatRate: Number(e.target.value) / 100 })}
                                                            >
                                                                <option value="0">%0</option>
                                                                <option value="1">%1</option>
                                                                <option value="10">%10</option>
                                                                <option value="20">%20</option>
                                                            </select>
                                                        </td>
                                                    )}
                                                    <td style={styles.td}>{(newItem.quantity * newItem.price).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</td>
                                                    <td style={styles.td}>
                                                        <button onClick={handleAddNewItem} style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Ekle</button>
                                                    </td>
                                                </tr>
                                            </>
                                        ) : (
                                            order.lineItems.map(item => {
                                                const img = getProductImage(item.productId);
                                                const barcode = getProductBarcode(item.productId);
                                                return (
                                                    <tr key={item.productId}>
                                                        <td style={styles.td}>
                                                            {img || item.imageUrl ? <img src={img || item.imageUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '4px' }} />}
                                                        </td>
                                                        <td style={styles.td}>
                                                            {item.productName || getProductName(item.productId)}
                                                            {item.isCustomOrderProduct && (
                                                                <div style={{ fontSize: '11px', color: '#d97706', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginTop: '4px' }}>Özel Sipariş / Çizim</div>
                                                            )}
                                                        </td>
                                                        <td style={styles.td}>{barcode}</td>
                                                        <td style={styles.td}>{item.quantity}</td>
                                                        <td style={styles.td}>{preparedQuantities[item.productId] || 0}</td>
                                                        <td style={styles.td}>{shippedQuantities[item.productId] || 0}</td>
                                                        <td style={styles.td}>{item.price.toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</td>
                                                        {order.isItemBasedVat && <td style={styles.td}>%{Math.round((item.vatRate || 0) * 100)}</td>}
                                                        <td style={styles.td}>{item.total.toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            <div style={{ padding: '28px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
                                    {/* Notes Section */}
                                    <div>
                                        <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sipariş Notu</h4>
                                        {isEditing ? (
                                            <textarea 
                                                style={{ ...styles.input, height: '100px', resize: 'none', padding: '12px' }} 
                                                value={editedNote} 
                                                onChange={e => setEditedNote(e.target.value)} 
                                                placeholder="Siparişle ilgili not ekleyin..."
                                            />
                                        ) : (
                                            <div style={{ padding: '16px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', minHeight: '100px', fontSize: '14px', color: '#444', lineHeight: '1.6' }}>
                                                {order.note || 'Not eklenmemiş.'}
                                            </div>
                                        )}
                                    </div>

                                    {/* Totals Section */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Finansal Özet</h4>
                                        
                                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{ color: '#64748b' }}>Ara Toplam</span>
                                                <span style={{ fontWeight: '600' }}>{(order.subTotal || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</span>
                                            </div>
                                            
                                            {isEditing ? (
                                                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', background: '#fffef3' }}>
                                                    <span style={{ color: '#64748b' }}>İskonto</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <input type="number" style={{ ...styles.input, width: '100px', padding: '6px 10px' }} value={editedDiscount} onChange={e => setEditedDiscount(e.target.value)} />
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{order.currency}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                (order.discount || 0) > 0 && (
                                                    <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', color: '#ef4444' }}>
                                                        <span>İskonto</span>
                                                        <span style={{ fontWeight: '600' }}>- {(order.discount || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</span>
                                                    </div>
                                                )
                                            )}

                                            <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{ color: '#64748b' }}>KDV {(!isEditing && !order.isItemBasedVat) ? `(%${order.taxRate || 0})` : ''}</span>
                                                <span style={{ fontWeight: '600' }}>{(order.vatAmount || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</span>
                                            </div>

                                            {isEditing ? (
                                                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', background: '#fffef3' }}>
                                                    <span style={{ color: '#64748b' }}>Kargo</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <input type="number" style={{ ...styles.input, width: '100px', padding: '6px 10px' }} value={editedShippingCost} onChange={e => setEditedShippingCost(e.target.value)} />
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{order.currency}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                (order.shippingCost || 0) > 0 && (
                                                    <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                                                        <span style={{ color: '#64748b' }}>Kargo Ücreti</span>
                                                        <span style={{ fontWeight: '600' }}>{(order.shippingCost || 0).toFixed(2)} {order.currency === 'USD' ? '$' : '₺'}</span>
                                                    </div>
                                                )
                                            )}

                                            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(to right, #4f46e5, #7c3aed)', color: 'white' }}>
                                                <span style={{ fontWeight: '700', fontSize: '16px' }}>GENEL TOPLAM</span>
                                                <span style={{ fontWeight: '800', fontSize: '24px' }}>
                                                    {isEditing ? (
                                                        ((editedItems.reduce((acc, item) => acc + (item.quantity * item.price), 0) - Number(editedDiscount)) * (1 + Number(editedTaxRate / 100)) + Number(editedShippingCost)).toFixed(2)
                                                    ) : (
                                                        (order.total || 0).toFixed(2)
                                                    )} {order.currency === 'USD' ? '$' : '₺'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'shipments' && (
                    <div style={styles.gridTwo}>
                        <div style={styles.infoCard}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                                <div style={{ background: '#e0f2fe', padding: '10px', borderRadius: '12px' }}>📦</div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Yeni Sevkiyat Oluştur</h3>
                            </div>
                            <div className="responsive-table-wrapper" style={{ marginTop: '16px' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Görsel</th>
                                            <th style={styles.th}>Ürün</th>
                                            <th style={styles.th}>Kalan</th>
                                            <th style={styles.th}>Gönderilecek</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.lineItems.filter(i => {
                                            const remaining = i.quantity - (shippedQuantities[i.productId] || 0);
                                            return remaining > 0;
                                        }).map(item => {
                                            const remaining = item.quantity - (shippedQuantities[item.productId] || 0);
                                            return (
                                                <tr key={item.productId}>
                                                    <td style={styles.td}>
                                                        {getProductImage(item.productId) ? <img src={getProductImage(item.productId)} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '4px' }} />}
                                                    </td>
                                                    <td style={styles.td}>{item.productName || getProductName(item.productId)}</td>
                                                    <td style={styles.td}>{remaining}</td>
                                                    <td style={styles.td}>
                                                        <input 
                                                            type="number" 
                                                            style={{ ...styles.input, padding: '8px 12px' }} 
                                                            max={remaining}
                                                            min="0"
                                                            value={shipmentItems[item.productId] || ''} 
                                                            onChange={e => handleShipmentItemChange(item.productId, e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '24px' }}>
                                <div style={styles.formGroup}>
                                    <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>Kargo Firması</label>
                                    <input placeholder="Örn: Aras Kargo" style={styles.input} value={cargoCompany} onChange={e => setCargoCompany(e.target.value)} />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>Takip No</label>
                                    <input placeholder="Takip Numarası" style={styles.input} value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} />
                                </div>
                            </div>
                            <button 
                                onClick={handleCreateShipment} 
                                style={{ 
                                    ...styles.button, 
                                    width: '100%', 
                                    marginTop: '12px',
                                    justifyContent: 'center'
                                }}
                                disabled={actionLoading || shipmentLoading}
                            >
                                {shipmentLoading ? 'Oluşturuluyor...' : '🚀 Sevkiyatı Başlat'}
                            </button>
                        </div>

                        <div style={{ ...styles.infoCard, padding: '0', overflow: 'hidden' }}>
                            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.5)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '12px' }}>🚚</div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Geçmiş Sevkiyatlar</h3>
                                </div>
                                {selectedShipments.length > 0 && (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={handleCombineShipmentsPDF} style={{ ...styles.button, background: '#3b82f6', padding: '8px 16px', fontSize: '12px' }}>
                                            📄 Seçili İrsaliyeleri Yazdır
                                        </button>
                                        <button onClick={handleCreateInvoice} style={{ ...styles.button, background: '#10b981', padding: '8px 16px', fontSize: '12px' }}>
                                            📄 Seçili ({selectedShipments.length}) Faturalaştır
                                        </button>
                                    </div>
                                )}
                            </div>
                            {shipments.length === 0 ? (
                                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                                    <div style={{ 
                                        fontSize: '48px', 
                                        marginBottom: '20px', 
                                        display: 'inline-flex',
                                        background: '#fef3c7',
                                        padding: '24px',
                                        borderRadius: '30px',
                                        lineHeight: 1
                                    }}>📦</div>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontWeight: '700' }}>Henüz Sevkiyat Yok</h4>
                                    <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>Bu siparişe ait herhangi bir sevkiyat kaydı bulunamadı. Yukarıdaki formdan yeni bir sevkiyat başlatabilirsiniz.</p>
                                </div>
                            ) : (
                                <div className="responsive-table-wrapper">
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...styles.th, width: '40px' }}>
                                                    <input type="checkbox" onChange={e => handleSelectAllShipments(e.target.checked)} />
                                                </th>
                                                <th style={styles.th}>Tarih</th>
                                                <th style={styles.th}>İrsaliye No</th>
                                                <th style={styles.th}>Kargo Bilgisi</th>
                                                <th style={{ ...styles.th, textAlign: 'right' }}>İşlem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {shipments.map(ship => (
                                                <tr key={ship.id}>
                                                    <td style={styles.td}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedShipments.includes(ship.id)}
                                                            onChange={() => handleToggleShipmentSelection(ship.id)}
                                                            disabled={ship.status === 'Faturalandı'}
                                                        />
                                                    </td>
                                                    <td style={styles.td}>{ship.shipmentDate ? new Date(ship.shipmentDate.seconds * 1000).toLocaleDateString() : '-'}</td>
                                                    <td style={styles.td}><strong>#{ship.id.substring(0, 8).toUpperCase()}</strong></td>
                                                    <td style={styles.td}>
                                                        <div style={{ fontWeight: '600' }}>{ship.cargoCompany || 'Belirtilmedi'}</div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>Takip No: {ship.trackingNumber || '-'}</div>
                                                    </td>
                                                    <td style={{ ...styles.td, textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                            <button 
                                                                onClick={() => generateShipmentPDF(companyInfo, order, ship, products)}
                                                                style={{ ...styles.secondaryButton, padding: '6px 12px' }}
                                                                title="İrsaliye PDF"
                                                            >
                                                                📄 PDF
                                                            </button>
                                                            {ship.status === 'Faturalandı' && (
                                                                <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700', background: '#dcfce7', padding: '4px 8px', borderRadius: '6px' }}>
                                                                    FATURALANDI
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'payments' && (
                    <div style={styles.gridTwo}>
                        <div style={styles.infoCard}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                                <div style={{ background: '#dcfce7', padding: '10px', borderRadius: '12px' }}>💰</div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Ödeme Al (Tahsilat)</h3>
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <div style={styles.formGroup}>
                                    <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>Tutar ({order.currency})</label>
                                    <input 
                                        type="number" 
                                        style={{ ...styles.input, fontSize: '24px', fontWeight: '800', color: '#10b981', border: '2px solid #dcfce7', background: '#f0fdf4' }} 
                                        value={paymentAmount} 
                                        onChange={e => setPaymentAmount(e.target.value)} 
                                        placeholder="0.00" 
                                    />
                                </div>
                                <button 
                                    onClick={handleReceivePayment} 
                                    style={{ 
                                        ...styles.button, 
                                        width: '100%', 
                                        marginTop: '12px', 
                                        background: '#10b981',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                                        justifyContent: 'center'
                                    }}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? 'Kaydediliyor...' : '💰 Ödemeyi Tahsil Et'}
                                </button>
                            </div>
                        </div>

                        <div style={{ ...styles.infoCard, padding: '0', overflow: 'hidden' }}>
                            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255, 255, 255, 0.5)' }}>
                                <div style={{ background: '#eef2ff', padding: '10px', borderRadius: '12px' }}>📜</div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Ödeme Geçmişi</h3>
                            </div>
                            {payments.length === 0 ? (
                                <div style={{ padding: '64px 24px', textAlign: 'center' }}>
                                    <div style={{ 
                                        fontSize: '48px', 
                                        marginBottom: '20px', 
                                        display: 'inline-flex',
                                        background: '#dcfce7',
                                        padding: '24px',
                                        borderRadius: '30px',
                                        lineHeight: 1
                                    }}>💳</div>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontWeight: '700' }}>Ödeme Bekleniyor</h4>
                                    <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>Henüz bir ödeme kaydı bulunmuyor. Sol taraftaki panelden tahsilat girebilirsiniz.</p>
                                </div>
                            ) : (
                                <div className="responsive-table-wrapper">
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Tarih</th>
                                                <th style={styles.th}>Tutar</th>
                                                <th style={styles.th}>Açıklama</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td style={styles.td}>{p.date ? new Date(p.date.seconds * 1000).toLocaleDateString() : '-'}</td>
                                                    <td style={styles.td}>
                                                        <span style={{ fontWeight: '700', color: '#10b981' }}>
                                                            {p.amount.toFixed(2)} {p.currency}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>{p.description || 'Sipariş Ödemesi'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'preparation' && (
                    <div style={styles.infoCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '12px' }}>🛠️</div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Sipariş Hazırlama (Stok Blokajı)</h3>
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b', background: '#f8fafc', padding: '8px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                ℹ️ Hazırlanan ürünler stokta <strong>Bloke</strong> edilerek satışı engellenir.
                            </div>
                        </div>
                        <div className="responsive-table-wrapper">
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Görsel</th>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>Sipariş</th>
                                        <th style={styles.th}>Hazırlanan</th>
                                        <th style={styles.th}>Stok Durumu</th>
                                        <th style={styles.th}>Hazırlanacak</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.lineItems.map(item => {
                                        const prepared = preparedQuantities[item.productId] || 0;
                                        const shipped = shippedQuantities[item.productId] || 0;
                                        const maxToPrepare = item.quantity - shipped - prepared;
                                        const stock = getProductStock(item.productId);
                                        const product = products.find(p => p.id === item.productId);
                                        const blocked = product?.blockedQuantity || 0;
                                        const available = stock - blocked;

                                        return (
                                            <tr key={item.productId}>
                                                <td style={styles.td}>
                                                    {getProductImage(item.productId) ? <img src={getProductImage(item.productId)} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} /> : <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '6px' }} />}
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{item.productName || getProductName(item.productId)}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>Barkod: {getProductBarcode(item.productId)}</div>
                                                </td>
                                                <td style={styles.td}>{item.quantity}</td>
                                                <td style={styles.td}>
                                                    <span style={{ color: prepared === item.quantity ? '#10b981' : '#f59e0b', fontWeight: '700' }}>
                                                        {prepared}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ fontSize: '12px' }}>
                                                        <span style={{ color: available < maxToPrepare ? '#ef4444' : '#64748b' }}>Mevcut: <strong>{available}</strong></span>
                                                        <br />
                                                        <span style={{ color: '#94a3b8', fontSize: '11px' }}>Bloke: {blocked}</span>
                                                    </div>
                                                </td>
                                                <td style={styles.td}>
                                                    <input 
                                                        type="number" 
                                                        style={{ ...styles.input, width: '100px', padding: '8px 12px' }} 
                                                        disabled={maxToPrepare <= 0}
                                                        value={prepareOrderItems[item.productId] || ''}
                                                        onChange={e => handlePrepareOrderItemChange(item.productId, e.target.value)}
                                                        placeholder={maxToPrepare <= 0 ? "Tamamlandı" : "Miktar..."}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={handlePrepareOrder} 
                                style={{ 
                                    ...styles.button, 
                                    background: '#06C167', 
                                    padding: '12px 32px',
                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                                    justifyContent: 'center'
                                }}
                                disabled={actionLoading || Object.values(prepareOrderItems).every(v => !v || v <= 0)}
                            >
                                {actionLoading ? 'İşleniyor...' : '📦 Seçili Ürünleri Hazırla / Bloke Et'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'returns' && (
                    <div style={styles.infoCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                            <div style={{ background: '#fee2e2', padding: '10px', borderRadius: '12px' }}>🔄</div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>İade / Değişim İşlemleri</h3>
                        </div>
                        <div style={{ background: '#fff7ed', padding: '16px', borderRadius: '12px', border: '1px solid #ffedd5', color: '#9a3412', fontSize: '14px', marginBottom: '24px' }}>
                            ⚠️ İade edilen ürünler stoklarınıza geri eklenir ve müşteri bakiyesi alacaklandırılır.
                        </div>
                        <div className="responsive-table-wrapper">
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Görsel</th>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>Sipariş</th>
                                        <th style={styles.th}>Daha Önce İade</th>
                                        <th style={styles.th}>İade Edilecek</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.lineItems.map(item => {
                                        const alreadyReturned = (order.returnedItems || {})[item.productId] || 0;
                                        const maxReturnable = item.quantity - alreadyReturned;

                                        return (
                                            <tr key={item.productId}>
                                                <td style={styles.td}>
                                                    {getProductImage(item.productId) ? <img src={getProductImage(item.productId)} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} /> : <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '6px' }} />}
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={{ fontWeight: '600' }}>{item.productName || getProductName(item.productId)}</div>
                                                </td>
                                                <td style={styles.td}>{item.quantity}</td>
                                                <td style={styles.td}>{alreadyReturned}</td>
                                                <td style={styles.td}>
                                                    <input 
                                                        type="number" 
                                                        style={{ ...styles.input, width: '100px', padding: '8px 12px' }} 
                                                        max={maxReturnable}
                                                        min="0"
                                                        value={returnItems[item.productId] || ''}
                                                        onChange={e => setReturnItems(prev => ({ ...prev, [item.productId]: e.target.value }))}
                                                        placeholder="0"
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginTop: '32px' }}>
                            <div style={styles.formGroup}>
                                <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>İade Nedeni</label>
                                <select 
                                    style={styles.input} 
                                    value={returnReason} 
                                    onChange={e => setReturnReason(e.target.value)}
                                >
                                    <option value="">Neden Seçin...</option>
                                    <option value="Hatalı Ürün">Hatalı Ürün</option>
                                    <option value="Vazgeçme">Vazgeçme</option>
                                    <option value="Yanlış Ürün Gönderimi">Yanlış Ürün Gönderimi</option>
                                    <option value="Diğer">Diğer</option>
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>İade Edilecek Depo (İsteğe Bağlı)</label>
                                <select 
                                    style={styles.input} 
                                    value={selectedReturnWarehouse} 
                                    onChange={e => setSelectedReturnWarehouse(e.target.value)}
                                >
                                    <option value="">Depo Seçin (Varsayılan)</option>
                                    {warehouses.map(w => (
                                        <option key={w.id} value={w.id}>{w.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={{ fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '8px', display: 'block' }}>Not</label>
                                <input placeholder="Ek notlar..." style={styles.input} value={returnNote} onChange={e => setReturnNote(e.target.value)} />
                            </div>
                        </div>
                        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={handleReturn} 
                                style={{ 
                                    ...styles.button, 
                                    background: '#ef4444',
                                    padding: '12px 32px',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                                    justifyContent: 'center'
                                }}
                                disabled={actionLoading || !returnReason || Object.values(returnItems).every(v => !v || v <= 0)}
                            >
                                {actionLoading ? 'İşleniyor...' : '🔄 İadeyi Onayla ve Stokları Güncelle'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div style={styles.infoCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
                            <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '12px' }}>📋</div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Sipariş Hareketleri (Zaman Çizelgesi)</h3>
                        </div>
                        <div style={{ padding: '0 10px' }}>
                            <OrderActivity companyId={userData.companyId} orderId={orderId} />
                        </div>
                    </div>
                )}
            </div>
            </div>

            <PDFOptionModal
                open={pdfModalOpen}
                onClose={() => setPdfModalOpen(false)}
                onSelect={(withImages, withPrices, withStock) => generateOrderPDF(companyInfo, order, products, withImages, withPrices, withStock)}
            />
        </div>
    );
};

function PDFOptionModal({ open, onClose, onSelect }) {
    const { hasRestriction } = useSubscription();
    const canUsePdfImages = hasRestriction('pdfImages');
    const [withStock, setWithStock] = useState(false);
    const [withPrices, setWithPrices] = useState(true);
    
    if (!open) return null;
    
    return (
        <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh', 
            background: 'rgba(15, 23, 42, 0.4)', 
            backdropFilter: 'blur(8px)',
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '20px',
            animation: 'fadeIn 0.3s ease'
        }}>
            <div style={{ 
                background: 'rgba(255, 255, 255, 0.95)', 
                backdropFilter: 'blur(20px)',
                padding: '40px', 
                borderRadius: '32px', 
                width: '100%',
                maxWidth: '440px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.7)',
                position: 'relative'
            }}>
                <button 
                    onClick={onClose} 
                    style={{ 
                        position: 'absolute', 
                        top: '24px', 
                        right: '24px', 
                        background: '#f1f5f9', 
                        border: 'none', 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        fontSize: '20px'
                    }}
                >
                    &times;
                </button>

                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '16px' }}>📋</div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>PDF Oluştur</h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Sipariş dökümü için seçenekleri belirleyin.</p>
                </div>

                <div style={{ 
                    background: '#f8fafc', 
                    padding: '20px', 
                    borderRadius: '20px',
                    marginBottom: '32px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                        <input 
                            type="checkbox" 
                            style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#4f46e5' }} 
                            checked={withStock} 
                            onChange={e => setWithStock(e.target.checked)} 
                        /> 
                        Stok Miktarlarını Göster
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                        <input 
                            type="checkbox" 
                            style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#4f46e5' }} 
                            checked={withPrices} 
                            onChange={e => setWithPrices(e.target.checked)} 
                        /> 
                        Fiyatları Göster
                    </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <button 
                        onClick={() => { onSelect(true, withPrices, withStock); onClose(); }} 
                        style={{ ...styles.button, width: '100%', height: '54px', justifyContent: 'center', fontSize: '15px' }} 
                        disabled={!canUsePdfImages}
                    >
                        🖼️ Resimli PDF İndir
                    </button>
                    <button 
                        onClick={() => { onSelect(false, withPrices, withStock); onClose(); }} 
                        style={{ ...styles.button, width: '100%', height: '54px', justifyContent: 'center', background: 'linear-gradient(135deg, #06C167, #4f46e5)', fontSize: '15px' }}
                    >
                        📄 Sadece Metin (Hızlı)
                    </button>
                    {!canUsePdfImages && (
                        <p style={{ fontSize: '11px', color: '#ef4444', textAlign: 'center', margin: '4px 0 0 0', fontWeight: '600' }}>
                            ⚠️ Resimli PDF abonelik gerektirir.
                        </p>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );
}

export default OrderDetail;
